// client/src/components/galeria/GaleriaManagementPage.js
import React, { useState, useEffect } from 'react';
import { useGaleria, useMutateGaleria, useCurrentImagem } from '../../hooks/useGaleria';
import { useSegmentos } from '../../hooks/useSegmentos';
import useAlert from '../../hooks/useAlert'; // ✅ CORREÇÃO 1: Importando 'useAlert' como default (sem chaves).
import Pagination from '../common/Pagination';
import GerarImagemIABox from '../ai/GerarImagemIABox';
import { resizeImageFileToSquare } from '../../utils/imageResize';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import {
    GalleryHorizontal, Edit2, Trash2, Loader2, Save, XCircle, Search, Eraser, Filter as FilterIcon
} from 'lucide-react';

// Maior das dimensões aceitas pela Galeria (ver allowedSizes em onFileChange
// abaixo) — a imagem gerada por IA nunca sai nessa dimensão exata, então é
// sempre reamostrada pra este tamanho antes de entrar no mesmo fluxo de
// upload do arquivo escolhido manualmente.
const TAMANHO_IMAGEM_IA = 1000;

// --- SUB-COMPONENTE: Formulário de Adicionar/Editar ---
const ImagemForm = ({ current, addImagem, updateImagem, clearCurrent, loading, onClose, segmentos }) => {
    // ✅ CORREÇÃO 2: O formulário agora usa seu próprio hook 'useAlert', não precisa mais receber como prop.
    const { setAlert } = useAlert();

    const initialState = { 
        nome: '', 
        descricao: '', 
        categorias: '', 
        segmento: '', 
        ean: '' 
    };
    const [imagemData, setImagemData] = useState(initialState);
    const [file, setFile] = useState(null);
    const [previewIA, setPreviewIA] = useState(null);

    useEffect(() => {
        if (current) {
            setImagemData({
                nome: current.nome || '',
                descricao: current.descricao || '',
                categorias: (current.categorias || []).join(', '),
                segmento: current.segmento?._id || current.segmento || '',
                ean: current.ean || ''
            });
        } else {
            setImagemData(initialState);
        }
    }, [current]);
    
    const onChange = e => setImagemData({ ...imagemData, [e.target.name]: e.target.value });
    
    const onFileChange = e => {
        const selectedFile = e.target.files[0];
        const inputElement = e.target;

        setFile(null);

        if (!selectedFile) return;

        if (!selectedFile.type.startsWith('image/')) {
            setAlert('Por favor, selecione um arquivo de imagem válido (PNG, JPG).', 'danger');
            inputElement.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                const allowedSizes = [150, 200, 250, 500, 1000];

                if (width !== height) {
                    setAlert('A imagem deve ser quadrada (largura igual à altura).', 'danger');
                    inputElement.value = '';
                    return;
                }

                if (!allowedSizes.includes(width)) {
                    const tamanhosPermitidos = allowedSizes.map(s => `${s}x${s}`).join(', ');
                    setAlert(`Tamanho de imagem inválido. As dimensões permitidas são: ${tamanhosPermitidos}.`, 'danger');
                    inputElement.value = '';
                    return;
                }

                setFile(selectedFile);
            };
            img.onerror = () => {
                setAlert('Não foi possível ler o arquivo. Por favor, tente um arquivo de imagem diferente.', 'danger');
                inputElement.value = '';
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(selectedFile);
    };

    const handleUseImagemIA = async (iaFile, iaPreviewUrl) => {
        try {
            const arquivoRedimensionado = await resizeImageFileToSquare(iaFile, TAMANHO_IMAGEM_IA);
            setFile(arquivoRedimensionado);
            setPreviewIA(iaPreviewUrl);
        } catch (err) {
            console.error('Erro ao redimensionar imagem gerada por IA:', err);
            setAlert('Erro ao preparar a imagem gerada. Tente novamente.', 'danger');
        }
    };

    const onSubmit = async e => {
        e.preventDefault();
        
        try {
            if (current) {
                await updateImagem.mutateAsync({
                    id: current._id,
                    data: {
                        ...imagemData,
                        categorias: imagemData.categorias.split(',').map(c => c.trim()).filter(Boolean)
                    }
                });
            } else {
                if (!file) {
                    return setAlert('Por favor, selecione um arquivo de imagem válido antes de salvar.', 'warning');
                }
                
                const formData = new FormData();
                formData.append('nome', imagemData.nome);
                formData.append('descricao', imagemData.descricao);
                formData.append('categorias', imagemData.categorias);
                formData.append('segmento', imagemData.segmento);
                formData.append('ean', imagemData.ean);
                formData.append('imagem', file);
                
                await addImagem.mutateAsync(formData);
            }
            
            onClose();
        } catch (err) {
            console.error('Erro ao salvar imagem:', err);
            // O hook useMutateGaleria já exibe um alerta de erro da API.
        }
    };

    return (
        <div className="content-panel mt-6">
            <form onSubmit={onSubmit}>
                <div className="form-header">
                    <h3 className="form-title">{current ? 'Editar Imagem da Galeria' : 'Adicionar Nova Imagem à Galeria'}</h3>
                    <button type="button" onClick={onClose} className="form-close-button" title="Fechar Formulário">
                        <XCircle/>
                    </button>
                </div>
                <fieldset className="form-section" disabled={loading}>
                    <div className="form-grid-container">
                        <div className="form-group">
                            <label>Nome*</label>
                            <input type="text" name="nome" value={imagemData.nome} onChange={onChange} className="form-input" required />
                        </div>
                        <div className="form-group">
                            <label>Segmento*</label>
                            <select 
                                name="segmento" 
                                value={imagemData.segmento} 
                                onChange={onChange} 
                                className="form-input"
                                required
                            >
                                <option value="">Selecione um segmento</option>
                                {segmentos?.map(segmento => (
                                    <option key={segmento._id} value={segmento._id}>
                                        {segmento.nome}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>EAN (Código de Barras)</label>
                            <input type="text" name="ean" value={imagemData.ean} onChange={onChange} className="form-input"/>
                        </div>
                        <div className="form-group full-width">
                            <label>Descrição</label>
                            <input type="text" name="descricao" value={imagemData.descricao} onChange={onChange} className="form-input"/>
                        </div>
                        <div className="form-group full-width">
                            <label>Categorias (separadas por vírgula)</label>
                            <input type="text" name="categorias" value={imagemData.categorias} onChange={onChange} className="form-input" placeholder="ex: depilação, laser, axilas"/>
                        </div>
                        {!current && (
                            <div className="form-group full-width">
                                <label>Arquivo de Imagem*</label>
                                <input
                                    type="file"
                                    name="file"
                                    id="imagem-file-input"
                                    onChange={(e) => { setPreviewIA(null); onFileChange(e); }}
                                    className="form-input"
                                    required={!file}
                                    accept="image/*"
                                />
                                {previewIA && (
                                    <div className="mt-2 flex items-center gap-3">
                                        <img src={previewIA} alt="Gerada por IA" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />
                                        <span className="form-hint">Imagem gerada por IA selecionada (redimensionada para {TAMANHO_IMAGEM_IA}x{TAMANHO_IMAGEM_IA}px).</span>
                                    </div>
                                )}
                                <GerarImagemIABox
                                    aspectRatio="1:1"
                                    aspectHint={`A imagem é sempre redimensionada para ${TAMANHO_IMAGEM_IA}x${TAMANHO_IMAGEM_IA}px (quadrada) antes de ser usada.`}
                                    placeholder="Descreva a imagem — ex: rosto feminino sorrindo durante depilação a laser, fundo clínico claro"
                                    onUseImage={handleUseImagemIA}
                                />
                            </div>
                        )}
                    </div>
                </fieldset>
                <div className="form-actions">
                    <button type="button" onClick={onClose} className="action-button action-button-secondary">
                        Cancelar
                    </button>
                    <button type="submit" className="action-button action-button-primary" disabled={loading}>
                        {loading ? <Loader2 className="animate-spin icon" /> : <Save className="icon" />}
                        {current ? 'Salvar Alterações' : 'Adicionar Imagem'}
                    </button>
                </div>
            </form>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL DA PÁGINA ---
const GaleriaManagementPage = () => {
    // Apenas os hooks de estado e de dados ficam no componente principal
    const [showForm, setShowForm] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({ 
        nome: '', 
        segmento: '', 
        categorias: '' 
    });
    const itemsPerPage = 12;

    const { currentImagem, setCurrent, clearCurrent } = useCurrentImagem();
    const { data: galeriaData, isLoading: isLoadingGaleria } = useGaleria({ 
        page: currentPage, 
        limit: itemsPerPage,
        ...filters
    });
    const { data: segmentos, isLoading: isLoadingSegmentos } = useSegmentos();
    
    const { 
        addImagem, 
        updateImagem, 
        deleteImagem 
    } = useMutateGaleria();

    const isMutationLoading = addImagem.isPending || updateImagem.isPending || deleteImagem.isPending;

    const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({ nome: '', segmento: '', categorias: '' });
        setCurrentPage(1);
    };
    
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleEdit = (imagem) => {
        setCurrent(imagem);
        setShowForm(true);
        setShowFilters(false);
    };

    const handleAddNew = () => {
        clearCurrent();
        setShowForm(true);
        setShowFilters(false);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        clearCurrent();
    };
    
    const handleToggleFilters = () => {
        setShowFilters(prev => !prev);
        if(showForm) setShowForm(false);
    };

    const imagens = galeriaData?.imagens || [];
    const totalItems = galeriaData?.count || 0; // Ajustado para usar 'count' da API
    const isLoading = isLoadingGaleria || isLoadingSegmentos;

    return (
        <div className="page-management-container">
            <PageHeader
                icon={<GalleryHorizontal />}
                title="Gerenciamento da Galeria"
                actions={!showForm && (
                    <button onClick={handleToggleFilters} className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}>
                        <FilterIcon size={18} className="icon"/>
                        {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
                    </button>
                )}
            />

            {showForm && (
                <ImagemForm 
                    current={currentImagem} 
                    addImagem={addImagem} 
                    updateImagem={updateImagem} 
                    clearCurrent={clearCurrent} 
                    loading={isMutationLoading} 
                    onClose={handleCloseForm} 
                    segmentos={segmentos}
                />
            )}

            {showFilters && !showForm && (
                <div className="content-panel mt-6">
                    <form onSubmit={handleFilterSubmit}>
                        <h3 className="panel-title">Filtrar Imagens</h3>
                        <div className="form-grid-container">
                            <div className="form-group">
                                <label htmlFor="nome-filtro">Pesquisar por Nome</label>
                                <div className="search-input-wrapper">
                                    <Search size={18} className="icon"/>
                                    <input 
                                        type="search" 
                                        id="nome-filtro" 
                                        name="nome" 
                                        value={filters.nome} 
                                        onChange={handleFilterChange} 
                                        placeholder="Digite o nome..." 
                                        className="form-input"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="segmento-filtro">Filtrar por Segmento</label>
                                <select 
                                    name="segmento" 
                                    id="segmento-filtro" 
                                    value={filters.segmento} 
                                    onChange={handleFilterChange} 
                                    className="form-input"
                                    disabled={isLoadingSegmentos}
                                >
                                    <option value="">Todos</option>
                                    {segmentos?.map(segmento => (
                                        <option key={segmento._id} value={segmento._id}>
                                            {segmento.nome}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="categorias-filtro">Filtrar por Categorias</label>
                                <input 
                                    type="text" 
                                    id="categorias-filtro" 
                                    name="categorias" 
                                    value={filters.categorias} 
                                    onChange={handleFilterChange} 
                                    placeholder="Ex: depilacao, laser" 
                                    className="form-input"
                                />
                            </div>
                        </div>
                        <div className="form-actions">
                            <button 
                                type="button" 
                                onClick={clearFilters} 
                                className="action-button action-button-secondary" 
                                disabled={isLoading}
                            >
                                <Eraser className="icon" /> Limpar
                            </button>
                            <button 
                                type="submit" 
                                className="action-button action-button-primary" 
                                disabled={isLoading}
                            >
                                <Search className="icon" /> Buscar
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            {!showForm && (
                <div className="content-panel mt-6">
                    <div className="table-header">
                        <h2>Resultados da Busca ({totalItems})</h2>
                    </div>
                    {isLoading ? (
                        <div className="panel-state-wrapper">
                            <Loader2 className="animate-spin"/>
                        </div>
                    ) : (
                        <>
                            <div className="image-gallery-grid mt-4">
                                {imagens.length > 0 ? (
                                    imagens.map(img => (
                                        <div key={img._id} className="gallery-item-card">
                                            <div className="gallery-item-card__image-container">
                                                <img src={img.url} alt={img.nome} />
                                            </div>
                                            <div className="gallery-item-card__body">
                                                <h5 title={img.nome}>{img.nome}</h5>
                                                <p>{img.descricao || 'Sem descrição'}</p>
                                                <div className="tags-container">
                                                    {(img.categorias || []).map(cat => (
                                                        <span key={cat} className="badge badge-blue">{cat}</span>
                                                    ))}
                                                </div>
                                                {img.ean && <p className="ean-code">EAN: {img.ean}</p>}
                                            </div>
                                            <div className="gallery-item-card__actions">
                                                <button 
                                                    onClick={() => handleEdit(img)} 
                                                    className="action-button action-button-secondary"
                                                >
                                                    <Edit2 size={16} className="icon"/>Editar
                                                </button>
                                                <button 
                                                    onClick={() => deleteImagem.mutate(img._id)} 
                                                    className="action-button action-button-delete"
                                                    disabled={deleteImagem.isPending}
                                                >
                                                    <Trash2 size={16} className="icon"/>
                                                    {deleteImagem.isPending ? 'Removendo...' : 'Remover'}
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="panel-state-wrapper panel-info">
                                        <Search size={24} className="icon"/>
                                        <span>Nenhuma imagem encontrada. Use os filtros para iniciar uma busca ou adicione uma nova.</span>
                                    </div>
                                )}
                            </div>
                            {totalItems > itemsPerPage && !isLoading && (
                                <Pagination 
                                    currentPage={currentPage} 
                                    totalItems={totalItems} 
                                    itemsPerPage={itemsPerPage} 
                                    onPageChange={paginate} 
                                />
                            )}
                        </>
                    )}
                </div>
            )}
            <FabButton onClick={handleAddNew} title="Adicionar à Galeria" visible={!showForm} />
        </div>
    );
};

export default GaleriaManagementPage;